import axios from 'axios'
import {
  CustomerMetadata,
  IMFiscalData,
  customerDto,
  favoriteAddressesDto,
  requestValidateConfirmDto,
  requestValidateDto,
  imFiscalDataListDto,
} from '../store/auth.dto'
import { BaseService } from '../base.service'
import { Address } from '../store/order/order.dto'
import { zAny } from '../store/common.dto'
import { UserDevice, userDevicesDto } from './devices.dto'
import { publicConfig } from '~/config/public'
import logRequests from '~/lib/axiosUtils'
import { CUSTOM_IM_ANALYTICS_ERRORS } from '~/lib/models/gtm'
import { extractCustomError } from '~/utils/gtmUtils'
import { DeviceData, deviceHeaders } from '~/utils/deviceUtils'

class ImService extends BaseService {
  readonly client
  readonly baseURL

  constructor() {
    super()
    this.baseURL = publicConfig.services.im.url
    this.client = axios.create({
      baseURL: publicConfig.services.im.url,
    })
    this.client.interceptors.response.use(
      res => res,
      error => {
        const { trackCustomError } = useGTM()
        trackCustomError(
          extractCustomError({ error, customHttpStatus: CUSTOM_IM_ANALYTICS_ERRORS.IM_API_ERROR })
        )
        return Promise.reject(error)
      }
    )
    logRequests('im', this.client)
  }

  getProfile(accessToken: string, deviceInfo: DeviceData) {
    return this.request(
      {
        method: 'get',
        url: '/profile',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...deviceHeaders(deviceInfo),
        },
      },
      customerDto
    )
  }

  logout(accessToken: string, deviceInfo: DeviceData) {
    return this.client({
      method: 'post',
      url: '/logout',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...deviceHeaders(deviceInfo),
      },
    })
  }

  async setIMProfile(
    {
      accessToken,
      metadata,
      checkCpf,
    }: {
      accessToken: string
      metadata: CustomerMetadata
      checkCpf?: boolean
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'patch',
        url: '/profile',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...deviceHeaders(deviceInfo),
        },
        data: { checkCpf, metadata },
      },
      customerDto
    )
  }

  // Favorite addresses
  async getFavoriteAddresses(
    { accessToken, country }: { accessToken: string; country: string },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'GET',
        url: '/profile/addresses',
        params: {
          country,
        },
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      favoriteAddressesDto
    )
  }

  async saveFavoriteAddress(
    {
      accessToken,
      address,
      country,
    }: {
      accessToken: string
      address: Address
      country: string
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'POST',
        url: '/profile/addresses',
        data: {
          ...address,
          country,
        },
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      favoriteAddressesDto
    )
  }

  async editFavoriteAddress(
    {
      accessToken,
      favoriteAddressId,
      address,
      country,
    }: {
      accessToken: string
      favoriteAddressId: string
      address: Address
      country: string
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'PATCH',
        url: `/profile/addresses/${favoriteAddressId}`,
        data: {
          ...address,
          country,
        },
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      favoriteAddressesDto
    )
  }

  async deleteFavoriteAddress(
    {
      accessToken,
      favoriteAddressId,
    }: {
      accessToken: string
      favoriteAddressId: string
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'DELETE',
        url: `/profile/addresses/${favoriteAddressId}`,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      favoriteAddressesDto
    )
  }

  /* Validation (phone, device, email...) */
  async validateData<T>(
    { accessToken, data }: { accessToken: string; data: T },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'POST',
        url: '/profile/data-validation-request',
        data,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      requestValidateDto
    )
  }

  async validateDataConfirm<T>(
    { accessToken, data }: { accessToken: string; data: T },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'POST',
        url: '/profile/data-confirmation-request',
        data,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      requestValidateConfirmDto // no body response for now
    )
  }

  /* Fiscal fields */
  async fetchFiscalFields(
    { accessToken, country }: { accessToken: string; country: string },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        url: `/profile/fiscalData?country=${country}`,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      imFiscalDataListDto
    )
  }

  async saveFiscalFields(
    {
      accessToken,
      fiscalData,
    }: {
      accessToken: string
      fiscalData: IMFiscalData
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'POST',
        url: '/profile/fiscalData',
        data: fiscalData,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      imFiscalDataListDto
    )
  }

  async updateFiscalFields(
    {
      accessToken,
      fiscalData,
      idFiscalFields,
    }: {
      accessToken: string
      fiscalData: IMFiscalData
      idFiscalFields: string
    },
    deviceInfo: DeviceData
  ) {
    return this.request(
      {
        method: 'PATCH',
        url: `/profile/fiscalData/${idFiscalFields}`,
        data: fiscalData,
        headers: { Authorization: `Bearer ${accessToken}`, ...deviceHeaders(deviceInfo) },
      },
      imFiscalDataListDto
    )
  }

  getUserDevices(
    { accessToken }: { accessToken: string },
    deviceInfo: DeviceData
  ): Promise<{ docs: UserDevice[] }> {
    return this.request(
      {
        method: 'get',
        url: '/profile/devices?sort=-updatedAt&limit=20&paginate=true&page=1',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...deviceHeaders(deviceInfo),
        },
      },
      userDevicesDto
    )
  }

  deleteUserDevices(
    {
      accessToken,
      deviceIdentifiers,
    }: {
      accessToken: string
      deviceIdentifiers: string[]
    },
    deviceInfo: DeviceData
  ): Promise<{ docs: UserDevice[] }> {
    return this.request(
      {
        method: 'POST',
        url: '/profile/close-device-sessions',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...deviceHeaders(deviceInfo),
        },
        data: { deviceIdentifiers },
      },
      userDevicesDto
    )
  }

  deleteAllUserSessions(
    { accessToken }: { accessToken: string },
    deviceInfo: DeviceData
  ): Promise<{ docs: UserDevice[] }> {
    return this.request(
      {
        method: 'DELETE',
        url: '/profile/close-all-sessions',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...deviceHeaders(deviceInfo),
        },
      },
      zAny // empty response 204
    )
  }

  removeTokenCookie() {
    return $fetch('/api/token', {
      method: 'POST',
    })
  }
}

export const imService = new ImService()
